import { Box, Chip, Stack, Typography } from '@mui/material';
import type { GameRelease } from '../lib/api/games-api-client';

/**
 * `<GameReleases>` — per-platform launch dates on the Game page.
 *
 * Three render modes based on the shape of the `releases` array:
 *   - Empty: returns `null`.
 *   - All platforms share one date: renders a single header line
 *     ("Released October 1, 2026") plus the platform chip strip.
 *   - Staggered launches: renders a "Launch schedule" header plus
 *     a grid of `{platform}: {date}` rows so the SPA conveys the
 *     release-wave differences cleanly.
 *
 * The top-of-page hero overlay already shows the EARLIEST date +
 * platform chips; this component is the detailed drill-down that
 * follows the summary.
 *
 * R3.7 — every function ≤20 non-comment lines.
 */

interface GameReleasesProps {
  readonly releases: ReadonlyArray<GameRelease>;
}

export function GameReleases({ releases }: GameReleasesProps): JSX.Element | null {
  if (releases.length === 0) return null;
  const uniqueDates = Array.from(new Set(releases.map((r) => r.date)));
  if (uniqueDates.length === 1) {
    return <UniformReleaseView date={uniqueDates[0]} platforms={releases.map((r) => r.platform)} />;
  }
  return <StaggeredReleaseView releases={releases} />;
}

interface UniformReleaseViewProps {
  readonly date: string;
  readonly platforms: ReadonlyArray<string>;
}

function UniformReleaseView({ date, platforms }: UniformReleaseViewProps): JSX.Element {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: { sm: 90 }, fontWeight: 500 }}>
        Released
      </Typography>
      <Typography variant="body2">
        {formatDate(date)} · {platforms.join(', ')}
      </Typography>
    </Stack>
  );
}

interface StaggeredReleaseViewProps {
  readonly releases: ReadonlyArray<GameRelease>;
}

function StaggeredReleaseView({ releases }: StaggeredReleaseViewProps): JSX.Element {
  const sorted = [...releases].sort((a, b) => a.date.localeCompare(b.date));
  return (
    <Stack spacing={1}>
      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
        Launch schedule
      </Typography>
      <Stack spacing={0.5}>
        {sorted.map((r) => (
          <Box key={`${r.platform}-${r.date}`} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip label={r.platform} size="small" variant="outlined" sx={{ minWidth: 72, fontWeight: 500 }} />
            <Typography variant="body2">{formatDate(r.date)}</Typography>
          </Box>
        ))}
      </Stack>
    </Stack>
  );
}

/** YYYY-MM-DD → `MMMM d, yyyy` in UTC. */
function formatDate(iso: string): string {
  const parsed = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return iso;
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
  });
}
